import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import GlobalTooltip from 'components/GlobalTooltip';
import { useToast } from 'context/ToastContext';
import debounce from 'lodash.debounce';
import { dollarFormat, fixToTwoDecimal, getUserId } from 'utils/utils';
import { getUserHourRate, createTimeEntry } from 'services/timerServices';
import { listCasesAndLeads } from 'services/CaseServices';
import { useFirmUserList } from 'hooks/useFirmUserList';
import useActivityTypes from 'hooks/useActivityTypes';
import { generateFullName } from 'utils/generateFullNameUtils';
import { convertToTimezoneWithStartOfDayFormatWithCurrentTime } from 'utils/utility_functions/timezone';
import './BulkEntriesDialog.css';
import { UserDetailsContext } from 'context/userDetailsContext';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'modules/BillingV2/config/queryClient';
import {
  TIME_ENTRY_DESCRIPTION_MAX_LENGTH,
  TIME_ENTRY_DURATION_INPUT_MAX_LENGTH,
  TIME_ENTRY_DURATION_OR_RATE_INPUT_MAX_VALUE,
  TIME_ENTRY_TOTAL_AMOUNT_MAX_VALUE,
  exceedsTimeEntryTotalAmountMax,
} from 'modules/BillingV2/TimeEntriesV2/timeEntriesConstantsV2';

/** Round to cents and stringify without scientific notation (avoids broken number inputs). */
function formatBulkHourlyRateValue(num) {
  const x = Number(num);
  if (!Number.isFinite(x) || x < 0) return '';
  const rounded = Math.round(x * 100) / 100;
  return String(rounded);
}

const DURATION_INPUT_PATTERN = /^\d{0,10}(\.\d{0,2})?$/;

/**
 * @param {Array<{ caseItem?: unknown; code?: unknown; duration?: string; description?: string; rate?: string; nonBill?: boolean }>} bulkRows
 * @param {boolean} isCaseOrLeadSpecific
 * @returns {string[]}
 */
function getBulkRowsValidationErrors(bulkRows, isCaseOrLeadSpecific) {
  const rowsWithData = bulkRows.filter((row) => row.caseItem || row.code || row.duration || row.description || row.rate);
  const errorMessages = [];
  const maxLabel = TIME_ENTRY_DURATION_OR_RATE_INPUT_MAX_VALUE.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const totalCapLabel = TIME_ENTRY_TOTAL_AMOUNT_MAX_VALUE.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  rowsWithData.forEach((row) => {
    const rowNumber = bulkRows.findIndex((r) => r === row) + 1;
    if (!isCaseOrLeadSpecific && !row.caseItem) errorMessages.push(`Row ${rowNumber}: Case is required`);

    const durNum = parseFloat(String(row.duration ?? '').trim());
    if (!row.duration || String(row.duration).trim() === '' || Number.isNaN(durNum) || durNum <= 0) {
      errorMessages.push(`Row ${rowNumber}: Duration is required and must be greater than 0`);
    } else {
      const dStr = String(row.duration).trim();
      if (!DURATION_INPUT_PATTERN.test(dStr)) {
        errorMessages.push(`Row ${rowNumber}: Duration may have up to 10 digits before the decimal and 2 after`);
      }
      if (durNum > TIME_ENTRY_DURATION_OR_RATE_INPUT_MAX_VALUE) {
        errorMessages.push(`Row ${rowNumber}: Duration cannot exceed ${maxLabel}`);
      }
    }

    const rateNum = parseFloat(String(row.rate ?? '').trim());
    const rStr = String(row.rate ?? '').trim();
    if (!row.nonBill) {
      if (!row.rate || rStr === '' || Number.isNaN(rateNum) || rateNum <= 0) {
        errorMessages.push(`Row ${rowNumber}: Hourly rate is required and must be greater than 0`);
      } else {
        if (!DURATION_INPUT_PATTERN.test(rStr)) {
          errorMessages.push(`Row ${rowNumber}: Hourly rate may have up to 10 digits before the decimal and 2 after`);
        }
        if (rateNum > TIME_ENTRY_DURATION_OR_RATE_INPUT_MAX_VALUE) {
          errorMessages.push(`Row ${rowNumber}: Hourly rate cannot exceed ${maxLabel}`);
        }
      }
    } else if (rStr !== '') {
      if (Number.isNaN(rateNum) || rateNum < 0) {
        errorMessages.push(`Row ${rowNumber}: Hourly rate must be a valid non-negative number`);
      } else {
        if (!DURATION_INPUT_PATTERN.test(rStr)) {
          errorMessages.push(`Row ${rowNumber}: Hourly rate may have up to 10 digits before the decimal and 2 after`);
        }
        if (rateNum > TIME_ENTRY_DURATION_OR_RATE_INPUT_MAX_VALUE) {
          errorMessages.push(`Row ${rowNumber}: Hourly rate cannot exceed ${maxLabel}`);
        }
      }
    }

    if (
      row.duration &&
      row.rate &&
      !Number.isNaN(durNum) &&
      durNum > 0 &&
      !Number.isNaN(rateNum) &&
      rateNum > 0 &&
      exceedsTimeEntryTotalAmountMax(durNum, rateNum)
    ) {
      errorMessages.push(`Row ${rowNumber}: Total amount cannot exceed ${totalCapLabel} (duration × hourly rate)`);
    }

    const desc = String(row.description ?? '');
    if (desc.length > TIME_ENTRY_DESCRIPTION_MAX_LENGTH) {
      errorMessages.push(`Row ${rowNumber}: Description cannot exceed ${TIME_ENTRY_DESCRIPTION_MAX_LENGTH} characters`);
    }
  });
  return errorMessages;
}

const BulkEntriesDialog = ({
  visible,
  onHide,
  userTimezone,
  caseFullList,
  searchCases,
  isAutomated,
  onSaved,
  caseView,
  caseDetails,
  isLeadSpecific,
}) => {
  const { addToast } = useToast();
  const { userList: firmUsersRaw } = useFirmUserList();
  const { activityTypes: allManualBillingCodes } = useActivityTypes({ category: 'manual', status: 'active' }, false);
  const userContext = useContext(UserDetailsContext);
  const queryClient = useQueryClient();

  // Determine if we're on a case/lead specific page
  const isCaseOrLeadSpecific = caseView || isLeadSpecific;

  const [bulkDate, setBulkDate] = useState(new Date());
  const [bulkForUser, setBulkForUser] = useState(null);
  const emptyBulkRow = useMemo(
    () => ({ caseItem: null, code: null, duration: '', description: '', rate: '', nonBill: false, rateAutoFilled: false }),
    []
  );
  const [bulkRows, setBulkRows] = useState([emptyBulkRow, emptyBulkRow, emptyBulkRow, emptyBulkRow, emptyBulkRow]);
  const [bulkSaveLoading, setBulkSaveLoading] = useState(false);
  const [bulkUserOptions, setBulkUserOptions] = useState([]);
  const [caseSuggestions, setCaseSuggestions] = useState([]);

  const bulkValidationErrors = useMemo(() => getBulkRowsValidationErrors(bulkRows, isCaseOrLeadSpecific), [bulkRows, isCaseOrLeadSpecific]);

  const bulkTotalAmountMaxLabel = useMemo(
    () =>
      TIME_ENTRY_TOTAL_AMOUNT_MAX_VALUE.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const bulkHasTotalAmountExceeded = useMemo(
    () =>
      bulkRows.some((row) => {
        const d = parseFloat(String(row.duration ?? '').trim());
        const r = parseFloat(String(row.rate ?? '').trim());
        if (!Number.isFinite(d) || d <= 0 || !Number.isFinite(r) || r <= 0) return false;
        return exceedsTimeEntryTotalAmountMax(d, r);
      }),
    [bulkRows]
  );

  const bulkSaveDisabled = useMemo(() => {
    if (bulkSaveLoading) return true;
    const hasRowActivity = bulkRows.some((row) => row.caseItem || row.code || row.duration || row.description || row.rate);
    if (!hasRowActivity) return false;
    if (bulkValidationErrors.length > 0) return true;
    if (!bulkDate || !bulkForUser) return true;
    return false;
  }, [bulkSaveLoading, bulkRows, bulkValidationErrors, bulkDate, bulkForUser]);

  // Debounced case/lead suggestions loader using listCasesAndLeads
  const debouncedFetchCaseSuggestions = useMemo(
    () =>
      debounce((keyword) => {
        const q = (keyword || '').trim();
        if (!q) {
          setCaseSuggestions([]);
          return;
        }
        listCasesAndLeads(true, '', false, q, 20, true, [], true)
          .then((response) => {
            const cases = Array.isArray(response?.data?.case_list) ? response.data.case_list : [];
            const leads = Array.isArray(response?.data?.lead_list) ? response.data.lead_list : [];
            const combined = [...cases, ...leads];
            const mapped = combined.map((item) => {
              const name = item?.case_description?.case_name || item?.title || item?.task_name || '';
              const label = item?.unique_number ? `${item.unique_number} ${name}` : name;
              return {
                label,
                value: name,
                id: item?.case_id,
                is_lead: Boolean(item?.is_lead),
                practice_area: item?.case_description?.case_practice_area || item?.practice_area || null,
              };
            });
            setCaseSuggestions(mapped);
          })
          .catch(() => {
            setCaseSuggestions([]);
          });
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedFetchCaseSuggestions.cancel();
    };
  }, [debouncedFetchCaseSuggestions]);

  // Refs for keyboard navigation per row/column
  const columnsOrder = useMemo(() => {
    // If on case/lead specific page, skip 'case' column
    return isCaseOrLeadSpecific
      ? ['code', 'duration', 'description', 'rate', 'nonBill']
      : ['case', 'code', 'duration', 'description', 'rate', 'nonBill'];
  }, [isCaseOrLeadSpecific]);
  const inputRefs = useRef([]);
  // Top-level refs
  const dateRef = useRef(null);
  const billForRef = useRef(null);

  // Total amount across rows
  const totalAmount = useMemo(() => {
    try {
      return bulkRows.reduce((sum, row) => {
        const duration = parseFloat(row?.duration || 0) || 0;
        const rate = parseFloat(row?.rate || 0) || 0;
        return sum + duration * rate;
      }, 0);
    } catch (e) {
      return 0;
    }
  }, [bulkRows]);

  // Ensure refs array matches rows length
  useEffect(() => {
    const rowsNeeded = bulkRows.length;
    if (!Array.isArray(inputRefs.current)) inputRefs.current = [];
    while (inputRefs.current.length < rowsNeeded) {
      inputRefs.current.push({
        case: React.createRef(),
        code: React.createRef(),
        duration: React.createRef(),
        description: React.createRef(),
        rate: React.createRef(),
        nonBill: React.createRef(),
      });
    }
    if (inputRefs.current.length > rowsNeeded) {
      inputRefs.current = inputRefs.current.slice(0, rowsNeeded);
    }
  }, [bulkRows.length]);

  const focusRefSafely = (refObj) => {
    const el = refObj?.current;
    if (!el) return;
    // Attempt common focus handles across PrimeReact and native inputs
    // AutoComplete/Dropdown often expose focus() or inputEl
    if (typeof el.focus === 'function') {
      el.focus();
      return;
    }
    if (el.inputEl && typeof el.inputEl.focus === 'function') {
      el.inputEl.focus();
      return;
    }
    if (el.input && typeof el.input.focus === 'function') {
      el.input.focus();
      return;
    }
    if (el.inputRef && el.inputRef.current && typeof el.inputRef.current.focus === 'function') {
      el.inputRef.current.focus();
      return;
    }
    if (el.getInput && typeof el.getInput === 'function') {
      const inp = el.getInput();
      if (inp && typeof inp.focus === 'function') inp.focus();
    }
  };

  const focusField = (rowIndex, fieldKey) => {
    const rowRefs = inputRefs.current?.[rowIndex];
    if (!rowRefs) return;
    const refToFocus = rowRefs[fieldKey];
    focusRefSafely(refToFocus);
  };

  const handleTabKey = (event, rowIndex, fieldKey) => {
    if (event.key !== 'Tab') return;
    // Override default so we can traverse custom components consistently
    event.preventDefault();
    const colIdx = columnsOrder.indexOf(fieldKey);
    if (colIdx === -1) return;
    if (event.shiftKey) {
      // Move backwards
      if (colIdx > 0) return focusField(rowIndex, columnsOrder[colIdx - 1]);
      if (rowIndex > 0) return focusField(rowIndex - 1, columnsOrder[columnsOrder.length - 1]);
      // at very first field: jump back to Bill for
      return focusRefSafely(billForRef);
    }
    // Move forward
    if (colIdx < columnsOrder.length - 1) return focusField(rowIndex, columnsOrder[colIdx + 1]);
    if (rowIndex < bulkRows.length - 1) return focusField(rowIndex + 1, columnsOrder[0]);
    // Last field of last row: do nothing special
  };

  // Handle Tab navigation for top controls (Date and Bill for)
  const handleTopTabKey = (event, fieldKey) => {
    if (event.key !== 'Tab') return;
    if (fieldKey === 'date') {
      // Tab from Date -> Bill for
      if (!event.shiftKey) {
        event.preventDefault();
        return focusRefSafely(billForRef);
      }
      return; // allow default Shift+Tab from Date
    }
    if (fieldKey === 'billFor') {
      // Shift+Tab from Bill for -> Date
      if (event.shiftKey) {
        event.preventDefault();
        return focusRefSafely(dateRef);
      }
      // Tab from Bill for -> First column of first row (case or billing code)
      event.preventDefault();
      return focusField(0, columnsOrder[0]);
    }
  };

  // Build user options and default
  useEffect(() => {
    if (Array.isArray(firmUsersRaw) && firmUsersRaw.length) {
      const currentUserId = typeof getUserId === 'function' ? getUserId() : undefined;
      const formatted = firmUsersRaw.map((item) => {
        const fullName = generateFullName(item);
        const isMe = item?.user_id === currentUserId;
        return { ...item, assignee_name: isMe ? `${fullName} (Me)` : fullName };
      });
      setBulkUserOptions(formatted);
      if (!bulkForUser && currentUserId) {
        const current = formatted.find((u) => u?.user_id === currentUserId);
        if (current) setBulkForUser(current);
      }
    } else {
      setBulkUserOptions([]);
    }
  }, [firmUsersRaw]);

  useEffect(() => {
    if (bulkForUser?.firm_user_id) {
      if (isCaseOrLeadSpecific && caseDetails?.case_id) {
        // For case/lead specific page, only fetch rate for rows that already have duration entered
        bulkRows.forEach((row, idx) => {
          // Only fetch rate if the row has duration value
          if (row.duration) {
            const caseItem = {
              id: caseDetails.case_id,
              practice_area: caseDetails?.case_description?.case_practice_area || caseDetails?.practice_area,
            };
            fetchHourlyRateForBulkRow(idx, caseItem, bulkForUser);
          }
        });
      } else {
        // For global page, fetch rate for rows that have both case selected AND duration entered
        bulkRows.forEach((row, idx) => {
          if (row.caseItem?.id && row.duration) {
            fetchHourlyRateForBulkRow(idx, row.caseItem, bulkForUser);
          }
        });
      }
    }
  }, [bulkForUser]);

  const fetchHourlyRateForBulkRow = async (rowIndex, caseItem, forUser = null) => {
    const user = forUser || bulkForUser;
    if (!caseItem?.id || !user?.firm_user_id) return;
    try {
      const response = await getUserHourRate(caseItem.id, user.firm_user_id);
      const rate = response?.data?.rate;
      const nextRate = Number(rate);
      if (Number.isFinite(nextRate) && nextRate > 0) {
        setBulkRows((prev) =>
          prev.map((row, idx) => {
            if (idx !== rowIndex) return row;
            const capped = Math.min(nextRate, TIME_ENTRY_DURATION_OR_RATE_INPUT_MAX_VALUE);
            return { ...row, rate: formatBulkHourlyRateValue(capped), rateAutoFilled: true };
          })
        );
      }
    } catch (error) {
      setBulkRows((prev) => prev.map((row, idx) => (idx === rowIndex ? { ...row, rateAutoFilled: false } : row)));
    }
  };

  const getFilteredBillingCodes = (caseItem) => {
    // If on case/lead specific page, use caseDetails practice area
    const practiceArea = isCaseOrLeadSpecific
      ? caseDetails?.case_description?.case_practice_area || caseDetails?.practice_area
      : caseItem?.practice_area;

    if (!practiceArea) {
      return allManualBillingCodes;
    }

    return allManualBillingCodes?.filter((code) => {
      if (!code.practice_area_ids || code.practice_area_ids?.length === 0) {
        return true;
      }
      if (code.practice_area_ids?.includes('ALL')) {
        return true;
      }
      if (code.practice_area_ids?.includes(practiceArea)) {
        return true;
      }
      return false;
    });
  };

  const handleBulkSave = async () => {
    // Validate mandatory fields for each row
    const rowsWithData = bulkRows.filter((row) => row.caseItem || row.code || row.duration || row.description || row.rate);
    if (rowsWithData.length === 0) {
      addToast(false, 'error', 'Failed', 'Please fill in at least one row');
      return;
    }

    const errorMessages = getBulkRowsValidationErrors(bulkRows, isCaseOrLeadSpecific);

    if (errorMessages.length > 0) {
      const maxErrors = 3;
      const displayErrors = errorMessages.slice(0, maxErrors);
      const remainingErrors = errorMessages.length - maxErrors;
      let message = displayErrors.join('\n');
      if (remainingErrors > 0) message += `\n... and ${remainingErrors} more error(s)`;
      addToast(false, 'error', 'Failed', message);
      return;
    }

    const validRows = isCaseOrLeadSpecific
      ? rowsWithData.filter((row) => row.duration && (row.nonBill || row.rate))
      : rowsWithData.filter((row) => row.caseItem && row.duration && (row.nonBill || row.rate));
    if (!bulkDate) {
      addToast(false, 'error', 'Failed', 'Please select a date');
      return;
    }
    if (!bulkForUser) {
      addToast(false, 'error', 'Failed', 'Please select a user to bill for');
      return;
    }

    setBulkSaveLoading(true);
    try {
      const dateToFormat = bulkDate ? new Date(bulkDate) : new Date();
      const formattedDate = convertToTimezoneWithStartOfDayFormatWithCurrentTime(dateToFormat, userContext?.userDetails?.timezone);
      const timeEntries = validRows.map((row) => {
        const duration = parseFloat(row.duration || 0) || 0;
        const rate = parseFloat(row.rate || 0) || 0;
        const amount = fixToTwoDecimal(duration * rate);

        // Use caseDetails if on case/lead specific page, otherwise use row's caseItem
        const caseId = isCaseOrLeadSpecific ? caseDetails?.case_id : row.caseItem?.id;
        const caseName = isCaseOrLeadSpecific ? caseDetails?.case_description?.case_name || caseDetails?.case_name : row.caseItem?.value;
        const isLead = isCaseOrLeadSpecific ? isLeadSpecific || false : row.caseItem?.is_lead || false;

        return {
          start_time: Date.now(), // Current timestamp in milliseconds
          case_id: caseId,
          case_name: caseName,
          is_lead: isLead,
          activity_type: row.code?.activity_type || '',
          activity_type_id: row.code?.activity_type_id || '',
          activity_type_code: row.code?.activity_type_code || '',
          total_hours: duration.toString(),
          hour_rate: rate.toString(),
          amount: String(amount),
          notes: row.description || '',
          billable: !row.nonBill,
          time_entry_date: formattedDate,
          team_member_cognito_id: bulkForUser?.user_id || bulkForUser?.cognito_id,
          team_member_firm_user_id: bulkForUser?.firm_user_id,
          team_member_id: bulkForUser?.user_id || bulkForUser?.cognito_id,
          team_member_name: bulkForUser?.assignee_name?.replace(' (Me)', '') || generateFullName(bulkForUser),
        };
      });
      const payload = { time_entry_status: 'OPEN', time_entries: timeEntries };
      await createTimeEntry(payload, false, true);
      // Invalidate time entries queries to refresh the list
      queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      onHide();
      setBulkRows([emptyBulkRow, emptyBulkRow, emptyBulkRow, emptyBulkRow, emptyBulkRow]);
      setBulkDate(new Date());
      onSaved?.();
      addToast(false, 'success', 'Success', 'Bulk time entries created successfully');
    } catch (error) {
      addToast(false, 'error', 'Failed', 'Failed to create bulk time entries');
    } finally {
      setBulkSaveLoading(false);
    }
  };

  // Clear all editable rows but preserve Date and Bill for
  const handleCloseDialog = () => {
    setBulkRows([emptyBulkRow, emptyBulkRow, emptyBulkRow, emptyBulkRow, emptyBulkRow]);
    setBulkDate(new Date());
    onHide?.();
  };

  // When dialog is closed externally, reset date to today
  useEffect(() => {
    if (!visible) {
      setBulkDate(new Date());
    }
  }, [visible]);

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      className="add-contact-dialog "
      header={
        <div className="d-flex align-items-center w-100">
          <i className="icon-window-filled modal-close-icon pointer F-size14" onClick={handleCloseDialog} />
        </div>
      }
      style={{ width: '80vw' }}
      closable={false}
    >
      <div className="container-fluid mx-0 p-0 overflow-auto" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="mb-3 modal-heading F-size16 text-bold">Add Bulk Time Entries</div>
        <div className="d-flex flex-wrap mb-2">
          <div className="d-flex flex-column me-3 mb-2 col-12 col-md-auto">
            <label className="mb-1">Date</label>
            <Calendar
              ref={dateRef}
              value={bulkDate}
              onChange={(e) => setBulkDate(e.value)}
              onKeyDown={(e) => handleTopTabKey(e, 'date')}
              showIcon
              className="input-shadow filter-calender"
            />
          </div>
          <div className="d-flex flex-column me-3 mb-2 col-12 col-md-auto">
            <label className="mb-1">Bill for</label>
            <Dropdown
              ref={billForRef}
              value={bulkForUser}
              onChange={(e) => setBulkForUser(e.value)}
              onKeyDown={(e) => handleTopTabKey(e, 'billFor')}
              options={bulkUserOptions}
              optionLabel="assignee_name"
              placeholder={bulkForUser?.assignee_name || 'Billing User'}
              className="searchable-dropdown"
              filter
              filterBy="assignee_name"
            />
          </div>
          {/* Info chip placed next to Bill for */}
        </div>
        <div className="d-flex align-items-center mb-2 flex-wrap gap-2">
          <i className="pi pi-info-circle me-2" style={{ color: '#6c757d', fontSize: '12px' }} />
          <span className="text-muted" style={{ fontSize: '12px' }}>
            Duration: 0.1 = 6 minutes.
          </span>
        </div>

        {/* PrimeReact DataTable for bulk entries */}
        <div className="datatable-responsive bulk-entries-table-container">
          <DataTable value={bulkRows} className="responsive-bulk-table p-datatable-responsive" style={{ width: '100%' }}>
            {!isCaseOrLeadSpecific && (
              <Column
                header={
                  <span>
                    Case <span className="text-danger">*</span>
                  </span>
                }
                body={(row, { rowIndex }) => (
                  <>
                    <span className="p-column-title text-break mt-1">
                      Case <span className="text-danger">*</span>
                    </span>

                    <AutoComplete
                      ref={inputRefs.current[rowIndex]?.case}
                      value={row.caseItem}
                      suggestions={caseSuggestions}
                      completeMethod={(e) => debouncedFetchCaseSuggestions(e?.query || '')}
                      onChange={(e) => {
                        const value = e?.value;
                        setBulkRows((prev) =>
                          prev.map((r, i) => (i === rowIndex ? { ...r, caseItem: value, rate: '', rateAutoFilled: false } : r))
                        );
                        if (typeof value === 'string') debouncedFetchCaseSuggestions(value || '');
                        // Fetch hourly rate only if duration is already entered
                        if (value?.id && row.duration && bulkForUser?.firm_user_id) {
                          fetchHourlyRateForBulkRow(rowIndex, value);
                        }
                      }}
                      onPaste={(e) => {
                        try {
                          const pasted = (e.clipboardData || window.clipboardData)?.getData('text');
                          debouncedFetchCaseSuggestions(pasted || '');
                        } catch (_) {}
                      }}
                      onKeyDown={(e) => handleTabKey(e, rowIndex, 'case')}
                      placeholder="Enter a case name..."
                      field="label"
                      className="w-100"
                      dropdown={false}
                      filter={false}
                      style={{ minWidth: 0, width: '100%', wordBreak: 'break-word' }}
                    />
                  </>
                )}
              />
            )}
            <Column
              header="Billing Code"
              body={(row, { rowIndex }) => (
                <>
                  <span className="p-column-title text-break mt-1">Billing code</span>

                  <GlobalTooltip tooltip={!isCaseOrLeadSpecific && !row.caseItem ? 'Select a case first' : undefined} position="top">
                    <Dropdown
                      ref={inputRefs.current[rowIndex]?.code}
                      value={row.code}
                      onChange={(e) => setBulkRows((prev) => prev.map((r, i) => (i === rowIndex ? { ...r, code: e.value } : r)))}
                      onKeyDown={(e) => handleTabKey(e, rowIndex, 'code')}
                      options={getFilteredBillingCodes(row.caseItem) || []}
                      optionLabel="activity_label"
                      placeholder="Select Billing Code"
                      className="searchable-dropdown w-100"
                      filter
                      filterBy="activity_label,activity_type,activity_type_code"
                      showClear={row.code ? true : false}
                      disabled={!isCaseOrLeadSpecific && !row.caseItem}
                      style={{ minWidth: 0, width: '100%', wordBreak: 'break-word' }}
                    />
                  </GlobalTooltip>
                </>
              )}
            />
            <Column
              header={
                <span>
                  Duration <span className="text-danger">*</span>
                </span>
              }
              body={(row, { rowIndex }) => (
                <>
                  <span className="p-column-title text-break mt-1">
                    Duration <span className="text-danger">*</span>
                  </span>
                  <div className="d-flex align-items-center gap-1">
                    <InputText
                      ref={inputRefs.current[rowIndex]?.duration}
                      value={row.duration}
                      maxLength={TIME_ENTRY_DURATION_INPUT_MAX_LENGTH}
                      onChange={(e) => {
                        let value = e.target.value;
                        if (value.startsWith('.')) value = '0' + value;
                        if (/^00/.test(value)) return;
                        if (value === '' || DURATION_INPUT_PATTERN.test(value)) {
                          setBulkRows((prev) => prev.map((r, i) => (i === rowIndex ? { ...r, duration: value } : r)));
                          if (value && !row.rateAutoFilled && bulkForUser?.firm_user_id) {
                            if (isCaseOrLeadSpecific && caseDetails?.case_id) {
                              const caseItem = {
                                id: caseDetails.case_id,
                                practice_area: caseDetails?.case_description?.case_practice_area || caseDetails?.practice_area,
                              };
                              fetchHourlyRateForBulkRow(rowIndex, caseItem, bulkForUser);
                            } else if (row.caseItem?.id) {
                              fetchHourlyRateForBulkRow(rowIndex, row.caseItem, bulkForUser);
                            }
                          }
                        }
                      }}
                      onKeyDown={(e) => handleTabKey(e, rowIndex, 'duration')}
                      placeholder="0.00"
                      className="flex-grow-1"
                      style={{ minWidth: 0, width: '100%', wordBreak: 'break-word' }}
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      pattern="^\d{0,10}(\.\d{0,2})?$"
                    />
                  </div>
                </>
              )}
            />
            <Column
              header="Description"
              body={(row, { rowIndex }) => (
                <>
                  <span className="p-column-title text-break mt-1">Description</span>
                  <InputText
                    ref={inputRefs.current[rowIndex]?.description}
                    value={row.description}
                    maxLength={TIME_ENTRY_DESCRIPTION_MAX_LENGTH}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next.length <= TIME_ENTRY_DESCRIPTION_MAX_LENGTH) {
                        setBulkRows((prev) => prev.map((r, i) => (i === rowIndex ? { ...r, description: next } : r)));
                      }
                    }}
                    onKeyDown={(e) => handleTabKey(e, rowIndex, 'description')}
                    placeholder="Enter description..."
                    className="w-100 bulk-description-input"
                    style={{ minWidth: 0, width: '100%' }}
                  />
                </>
              )}
            />
            <Column
              header={<span>Hourly Rate</span>}
              body={(row, { rowIndex }) => (
                <>
                  <span className="p-column-title text-break mt-1">
                    Hourly Rate {!row.nonBill ? <span className="text-danger">*</span> : null}
                  </span>
                  <div className="d-flex align-items-center gap-1">
                    <InputText
                      ref={inputRefs.current[rowIndex]?.rate}
                      value={row.rate}
                      maxLength={TIME_ENTRY_DURATION_INPUT_MAX_LENGTH}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          setBulkRows((prev) => prev.map((r, i) => (i === rowIndex ? { ...r, rate: '', rateAutoFilled: false } : r)));
                          return;
                        }
                        let t = raw.startsWith('.') ? `0${raw}` : raw;
                        if (/^00/.test(t)) return;
                        if (!DURATION_INPUT_PATTERN.test(t)) return;
                        const num = parseFloat(t);
                        if (!Number.isNaN(num) && num > TIME_ENTRY_DURATION_OR_RATE_INPUT_MAX_VALUE) return;
                        setBulkRows((prev) => prev.map((r, i) => (i === rowIndex ? { ...r, rate: t, rateAutoFilled: false } : r)));
                      }}
                      onKeyDown={(e) => handleTabKey(e, rowIndex, 'rate')}
                      placeholder="0.00"
                      className={`flex-grow-1 ${row.rateAutoFilled ? 'auto-filled-rate' : ''}`}
                      title={
                        row.nonBill
                          ? 'Optional when non-billable'
                          : row.rateAutoFilled
                          ? 'Rate auto-filled from case settings (editable)'
                          : 'Required field'
                      }
                      style={{
                        backgroundColor: row.rateAutoFilled ? '#f0f8ff' : 'transparent',
                        minWidth: 0,
                        width: '100%',
                        wordBreak: 'break-word',
                      }}
                      inputMode="decimal"
                      pattern="^\d{0,10}(\.\d{0,2})?$"
                    />
                  </div>
                </>
              )}
            />
            <Column
              header="Amount"
              body={(row, { rowIndex }) => {
                const duration = parseFloat(row.duration || 0) || 0;
                const rate = parseFloat(row.rate || 0) || 0;
                const amount = fixToTwoDecimal(duration * rate);
                return (
                  <>
                    <span className="p-column-title text-break">Amount</span>
                    <span className="w-100" style={{ wordBreak: 'break-word' }}>
                      {dollarFormat(amount)}
                    </span>
                  </>
                );
              }}
            />
            <Column
              header="Non-Billable"
              body={(row, { rowIndex }) => (
                <>
                  <span className="p-column-title text-break">Non-Bill</span>
                  <input
                    ref={inputRefs.current[rowIndex]?.nonBill}
                    type="checkbox"
                    checked={row.nonBill}
                    onChange={(e) => setBulkRows((prev) => prev.map((r, i) => (i === rowIndex ? { ...r, nonBill: e.target.checked } : r)))}
                    onKeyDown={(e) => handleTabKey(e, rowIndex, 'nonBill')}
                    style={{ minWidth: 0 }}
                  />
                </>
              )}
            />
          </DataTable>
        </div>

        <div
          className="d-flex justify-content-end align-items-center mt-2"
          style={{ width: '100%', overflow: 'hidden', padding: '0 1rem', boxSizing: 'border-box' }}
        >
          <div className="F-size16 text-bold" style={{ maxWidth: '100%', whiteSpace: 'nowrap' }}>
            Total: {dollarFormat(totalAmount)}
          </div>
        </div>
        <div className="footer-btn">
          <div className="w-100 px-3 mb-2">
            {bulkHasTotalAmountExceeded && (
              <small className="text-danger d-block mt-2">
                Total amount (duration × hourly rate) per row cannot exceed {bulkTotalAmountMaxLabel}.
              </small>
            )}
            {bulkValidationErrors.length > 0 && (
              <ul className="small text-danger mb-0 ps-3 mt-2" style={{ listStyleType: 'disc' }}>
                {bulkValidationErrors.slice(0, 5).map((msg, idx) => (
                  <li key={idx}>{msg}</li>
                ))}
                {bulkValidationErrors.length > 5 && <li className="text-muted">…and {bulkValidationErrors.length - 5} more</li>}
              </ul>
            )}
          </div>
          <div className="d-flex justify-content-between align-items-center w-100 gap-2" style={{ padding: '0 1rem' }}>
            <Button
              type="button"
              label="+ Add 5 More Lines"
              className="p-button p-button-secondary p-button-sm"
              onClick={() => setBulkRows((prev) => [...prev, emptyBulkRow, emptyBulkRow, emptyBulkRow, emptyBulkRow, emptyBulkRow])}
            />
            <div className="d-flex align-items-center">
              <Button
                label="Cancel"
                className="p-button p-button-secondary p-button-sm outline me-2"
                onClick={handleCloseDialog}
                disabled={bulkSaveLoading}
              />
              <Button
                label={bulkSaveLoading ? 'Saving...' : 'Save'}
                className="p-button p-button-secondary p-button-sm"
                onClick={handleBulkSave}
                disabled={bulkSaveDisabled}
                loading={bulkSaveLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default BulkEntriesDialog;
